// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ExtendedComponentModel } from 'src/app/_interfaces/component-model-extension';
import { AnalysisModel } from 'src/app/api/remote-api/.patternmatch.generated/models/analysis-model';
import { PatternMatchModel } from 'src/app/api/remote-api/.patternmatch.generated/models/pattern-match-model';
import { pmGetAnalysis } from '../../../../api/remote-api/.patternmatch.generated/fn/pattern-match/pm-get-analysis';
import { pmGetSrsButtonToggle } from '../../../../api/remote-api/.patternmatch.generated/fn/pattern-match/pm-get-srs-button-toggle';
import { pmGetSrsLastTriggerTime } from '../../../../api/remote-api/.patternmatch.generated/fn/pattern-match/pm-get-srs-last-trigger-time';
import { pmsrsTriggerDataCollection } from '../../../../api/remote-api/.patternmatch.generated/fn/pattern-match/pmsrs-trigger-data-collection';
import {
  DryCool,
  fourK,
  tenK,
} from '../../../../constants/mrq-table-tooltip/magnet-type-legend';
import { RouteService } from '../../../portal/services/routeService.service';
import { SystemOverviewService } from '../../../portal/services/systemOverview.service';
import { MapApiService } from '../../../shared/services/map-api.service';
import { DeepReadonly } from '../../../shared/utils/api-call-state.util';
import { MouseEventData } from '../../components/pm-chart/pm-chart.component';

export interface PatternMatchModelWithId extends PatternMatchModel {
  id: number;
}
export interface AnalysisModelWithId
  extends Omit<AnalysisModel, 'patternMatches'> {
  patternMatches: DeepReadonly<Array<PatternMatchModelWithId>>;
}
@Injectable()
export class PatternMatchDataService {
  systemOverviewService = inject(SystemOverviewService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  routeService = inject(RouteService);
  mapApiService = inject(MapApiService);

  // BehaviorSubject to share MRQ dashboard URL from UsefulApplications
  private _mrqDashboardUrl$ = new BehaviorSubject<string>('');
  mrqDashboardUrl$ = this._mrqDashboardUrl$.asObservable();

  // Method to update the MRQ dashboard URL
  updateMrqDashboardUrl(url: string): void {
    this._mrqDashboardUrl$.next(url);
  }

  srsButtonToggle = this.mapApiService.mapEndpoint(pmGetSrsButtonToggle);

  srsLastTriggerTime = this.mapApiService.mapEndpoint(pmGetSrsLastTriggerTime);

  srsTriggerCollection = this.mapApiService.mapEndpoint(
    pmsrsTriggerDataCollection,
  );

  pmAnalysis = this.mapApiService.mapEndpoint(pmGetAnalysis, {
    customCacheKey: keyPMAnalysis,
    processResult(apiResult): Observable<AnalysisModelWithId> {
      let id = 0;
      return apiResult.pipe(
        map((r) => {
          return {
            ...r,
            patternMatches: r.patternMatches.map((m) => {
              return {
                id: ++id, // we give items an id so they can be reidentified
                ...m,
              };
            }),
          };
        }),
      );
    },
  });

  /**
   * Emits a flat array of all solutionPropositionContents from all patternMatches in the pmAnalysis result.
   */
  solutionPropositionContents$ = this.pmAnalysis.lastSuccessfulData$.pipe(
    map(
      (analysis: AnalysisModelWithId | undefined) =>
        analysis?.patternMatches.flatMap(
          (pm) =>
            pm.solutionPropositions?.flatMap(
              (sp) => sp.solutionPropositionContents,
            ) ?? [],
        ) ?? [],
    ),
  );

  /**
   * Emits only the first urlToWebsite string from solutionPropositionContents$ that contains 'https://sdtb.siemens-healthineers.com'.
   */
  readonly validSdtbUrl$ = this.solutionPropositionContents$.pipe(
    map(
      (contents) =>
        contents.find(
          (item) =>
            item.isUrlToWebSite &&
            item.urlToWebsite.includes('https://sdtb.siemens-healthineers.com'),
        )?.urlToWebsite ?? null,
    ),
  );

  systemDetails$ = this.systemOverviewService.systemDetails.lastSuccessfulData$;
  selectedBar$ = new BehaviorSubject<MouseEventData | undefined>(undefined);

  // Observable that combines the entire system overview and the current component ID.
  component$ = combineLatest({
    entireOverview:
      this.systemOverviewService.entireOverview.lastSuccessfulData$, // Fetches the entire system overview.
    componentId: this.routeService.currentComponent$, // Fetches the current component ID from the route service.
  }).pipe(
    map(({ componentId, entireOverview }) =>
      // Finds the component in the system overview that matches the current component ID.
      entireOverview.components.find((c) => c.id === componentId),
    ),
  );

  // Observable that maps the component to its filter name or display name.

  filterName$ = this.component$.pipe(
    map(
      (component) => component?.filterName ?? component?.displayName ?? 'n.a.',
    ),
  );

  goToComponent(
    component: DeepReadonly<ExtendedComponentModel>,
    route?: ActivatedRoute,
  ): void {
    this.router.navigate(['component', component.id], {
      relativeTo: route,
    });
  }

  /**
   * Gets the tooltip text for a given magnet type
   */
  getMagnetTypeLegend(magnetType: string | null | undefined): string {
    const normalizedType = magnetType?.toString().toLowerCase();

    switch (normalizedType) {
      case '10k':
        return tenK.toolTipText;
      case 'drycool':
        return DryCool.toolTipText;
      case '4k':
        return fourK.toolTipText;
      default:
        return fourK.toolTipText;
    }
  }
}

export function keyPMAnalysis(params: {
  systemSerialNumber: string;
  systemMaterialNumber: number;
  rangeInDays: number;
  modality: string;
  componentName: string;
}): string {
  return JSON.stringify(params);
}
